import {z} from 'zod';
import {
	editEffectsSortOrder,
	type EditEffectsSortOrderParams,
	getSortField,
	type GetSortFieldParams,
	getSortOrder,
	type GetSortOrderParams,
	SortOrder,
	SortSchema,
} from '@netx/core-schema';
// import {type IUser} from './userSchema';
import {typedEntries} from '@netx/core-utils';

export const USERS_SORTABLE_FIELDS = [
	'firstName',
	'lastName',
	'email',
	'userLevel',
	'login',
	'expirationDate',
	'loginDate',
	'isLoggedIn',
] as const;

export const UsersSortSchema = SortSchema.omit({
	field: true,
}).extend({
	field: z.enum(USERS_SORTABLE_FIELDS),
});

export type UsersSort = z.infer<typeof UsersSortSchema>;

export const UsersSortableField = z.enum(USERS_SORTABLE_FIELDS).enum;

export const USERS_DEFAULT_SORT_FIELD = UsersSortableField.login;

export const USERS_DEFAULT_SORT_ORDER = SortOrder.ASCENDING;

export const UserSortToUserKeyMap = {
	firstName: 'name1',
	lastName: 'name3',
	email: 'email',
	login: 'login',
	userLevel: 'type',
	expirationDate: 'expirationDate',
	disabled: 'disabled',
	loginDate: 'loginDate',
	isLoggedIn: 'isLoggedIn',
	groupIds: 'groupIds',
} as const;

export const UserKeyToUserSortMap = typedEntries(UserSortToUserKeyMap).reduce(
	(acc, [sortKey, userKey]) => {
		acc[userKey] = sortKey;
		return acc;
	},
	{} as Record<
		(typeof UserSortToUserKeyMap)[keyof typeof UserSortToUserKeyMap],
		keyof typeof UserSortToUserKeyMap
	>,
);

export const isUserFieldSortable = (field: string) => {
	return (
		USERS_SORTABLE_FIELDS.includes(
			field as (typeof USERS_SORTABLE_FIELDS)[number],
		) ||
		USERS_SORTABLE_FIELDS.includes(
			UserKeyToUserSortMap[
				field as keyof typeof UserKeyToUserSortMap
			] as (typeof USERS_SORTABLE_FIELDS)[number],
		)
	);
};

/** will return the sort passed in if valid/specified, otherwise will return the defaults for the invalid/unspecified */
export const getUsersDefaultSort = (
	sort: Partial<UsersSort> = {},
): UsersSort => ({
	field: getUsersSortField({sortField: sort.field}),
	order: getUsersSortOrder({sortOrder: sort.order}),
});

/** will return the passed in sort field if valid, otherwise will retutn the default sort field */
export const getUsersSortField = (
	params: Omit<
		GetSortFieldParams<(typeof USERS_SORTABLE_FIELDS)[number]>,
		'defaultSortField' | 'sortableFields'
	>,
) =>
	getSortField({
		...params,
		sortableFields: USERS_SORTABLE_FIELDS,
		defaultSortField: USERS_DEFAULT_SORT_FIELD,
	});

/** will return the passed in sort order if valid, otherwise will return the default sort order */
export const getUsersSortOrder = (
	params: Omit<GetSortOrderParams, 'defaultSortOrder'>,
) =>
	getSortOrder({
		...params,
		defaultSortOrder: USERS_DEFAULT_SORT_ORDER,
	});

/** given the original data, the new data, and the current sort, determine if the sort order was effected */
export const userEditEffectsOrder = (
	// TODO: the soretable and filterable keys differ and so we can't use `IUser` here
	// we will be rectifying the user bean one of these days and will make these match
	params: Omit<
		EditEffectsSortOrderParams<any, (typeof USERS_SORTABLE_FIELDS)[number]>,
		'sortableFields'
	>,
) => {
	// typedEntries(params.sort).forEach(([key]) => {
	// 	const userKey = UserSortToUserKeyMap[key];
	// 	// @ts-expect-error - we need to be able to index with the filter keys, which are different than the user keys, so we need to do a translation step - this is the translation step
	// 	if (userKey) mappedFilters[userKey] = params.filters[key];
	// });
	return editEffectsSortOrder({
		...params,
		sortableFields: USERS_SORTABLE_FIELDS,
	});
};
